import {useRef, useEffect, useCallback} from 'react';
import {useSpring} from '@react-spring/web';
import {
    isTouchesInsideRect,
    getMiddleTouchOnElement,
    getLengthOfLine,
    clamp,
} from 'utils';

export function useZoom({minScale, maxScale, onScale}: any) {
    const element = useRef<any>(null);
    const initialBoundingRect = useRef<any>(null);
    const firstTouch = useRef<any>(null);
    const initialPinchLength = useRef<any>(null);

    const [{scale, middleTouchOnElement, translateX, translateY}, api] =
        useSpring(() => ({
            scale: 1,
            middleTouchOnElement: [0, 0],
            translateX: 0,
            translateY: 0,
            immediate: true,
            // react-spring v9: onFrame -> onChange.
            onChange: (values: any) => {
                const currentScale = values?.scale ?? values?.value?.scale;
                if (typeof onScale === 'function' && currentScale != null) {
                    onScale({scale: currentScale});
                }
            },
        }));

    const handleTouchStart = useCallback(
        (event: any) => {
            if (event.touches.length !== 2) {
                return;
            }

            initialBoundingRect.current =
                element.current.getBoundingClientRect();

            if (
                !event.touches.length ||
                !isTouchesInsideRect(event.touches, initialBoundingRect.current)
            ) {
                return;
            }

            event.preventDefault();

            const [touch1, touch2] = event.touches;
            const {clientX, clientY} = getMiddleTouchOnElement(
                event.touches,
                initialBoundingRect.current
            );

            firstTouch.current = [clientX, clientY];
            initialPinchLength.current = getLengthOfLine(touch1, touch2);

            api.start({middleTouchOnElement: [clientX, clientY], immediate: true});
        },
        [api]
    );

    const handleTouchMove = useCallback(
        (event: any) => {
            if (firstTouch.current) {
                const currentMiddleTouchOnElement = getMiddleTouchOnElement(
                    event.touches,
                    initialBoundingRect.current
                );

                const [touch1, touch2] = event.touches;
                const currentPinchLength = getLengthOfLine(touch1, touch2);

                api.start({
                    scale: clamp(
                        currentPinchLength / initialPinchLength.current,
                        minScale,
                        maxScale
                    ),
                    translateX:
                        currentMiddleTouchOnElement.clientX -
                        firstTouch.current[0],
                    translateY:
                        currentMiddleTouchOnElement.clientY -
                        firstTouch.current[1],
                    immediate: true,
                });
            }
        },
        [api, minScale, maxScale]
    );

    const handleTouchEnd = useCallback(() => {
        api.start({
            scale: 1,
            translateX: 0,
            translateY: 0,
            immediate: false,
        });

        firstTouch.current = null;
        initialPinchLength.current = null;
        initialBoundingRect.current = null;
    }, [api]);

    useEffect(() => {
        element.current.ontouchstart = handleTouchStart;
        element.current.ontouchmove = handleTouchMove;
        element.current.ontouchend = handleTouchEnd;
    }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

    return [element, scale, translateX, translateY, middleTouchOnElement] as const;
}
